import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
    Box, Typography, Container, Paper, Button, CircularProgress,
    Alert, TextField, Grid, MenuItem, Divider
} from '@mui/material';
// Removed ContentCopyIcon, LinkIcon, Tooltip, InputAdornment if not used by new form

import authFetch from '../utils/authFetch';
import BrandingManager from '../components/BrandingManager'; // ADD THIS LINE
import logger from '../utils/logger';
import { useAuth } from '../contexts/AuthContext'; // To get current tenant info

// Define roles for the dropdown - values should match UserRole enum in backend
const staffRoles = [
    { value: 'tenant_admin', label: 'Tenant Administrator' },
    { value: 'physician_qhp', label: 'Physician (QHP)' },
    { value: 'clinical_staff', label: 'Clinical Staff (Non-QHP)' },
];

const inviteUserApi = async (inviteData) => {
    logger.debug("TenantSettings: Calling API to invite user...", inviteData);
    // The endpoint might be /api/admin/users/invite-staff or similar
    // Ensure this matches the backend router you've set up
    const data = await authFetch('/api/admin/users/invite-staff', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(inviteData),
    });
    logger.debug("TenantSettings: Invite user API response:", data);
    return data;
};

function TenantSettings() {
    const { user, selectedTenant } = useAuth();
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteFirstName, setInviteFirstName] = useState('');
    const [inviteLastName, setInviteLastName] = useState('');
    const [inviteRole, setInviteRole] = useState('');
    const [fieldErrors, setFieldErrors] = useState({});
    const [inviteSuccessMessage, setInviteSuccessMessage] = useState('');

    const inviteUserMutation = useMutation({
        mutationFn: inviteUserApi,
        onSuccess: (data) => {
            logger.info("Successfully invited user:", data);
            setInviteSuccessMessage(data.message || "Invitation sent successfully!");
            // Clear form
            setInviteEmail('');
            setInviteFirstName('');
            setInviteLastName('');
            setInviteRole('');
            setFieldErrors({});
            setTimeout(() => setInviteSuccessMessage(''), 5000);
        },
        onError: (error) => {
            logger.error("Error inviting user:", error);
            setInviteSuccessMessage(''); // Clear any previous success
            if (error.response && error.response.data && error.response.data.detail) {
                 setFieldErrors({ form: error.response.data.detail });
            } else {
                 setFieldErrors({ form: error.message || 'Failed to send invitation. Please try again.' });
            }
        },
    });

    const handleInviteUser = (event) => {
        event.preventDefault();
        const currentFieldErrors = {};
        if (!inviteEmail) currentFieldErrors.email = "Email is required.";
        else if (!/\S+@\S+\.\S+/.test(inviteEmail)) currentFieldErrors.email = "Invalid email format.";
        if (!inviteRole) currentFieldErrors.role = "Role is required.";

        setFieldErrors(currentFieldErrors);

        if (Object.keys(currentFieldErrors).length === 0) {
            setInviteSuccessMessage(''); // Clear previous messages
            inviteUserMutation.mutate({
                email: inviteEmail,
                first_name: inviteFirstName,
                last_name: inviteLastName,
                role_to_assign: inviteRole,
            });
        }
    };

    // Display current tenant name if available from AuthContext
    const currentTenantName = user?.memberships?.find(m => m.tenant_id === user.activeTenantId)?.tenant_name || "Your Organization";


    return (
        <Container maxWidth="md">
            <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 }, mt: 4 }}>
                <Typography component="h1" variant="h4" gutterBottom>
                    Tenant Settings
                </Typography>
                <Typography variant="body1" color="text.secondary" gutterBottom>
                    Manage settings and user invitations for {currentTenantName}.
                </Typography>

                <Divider sx={{ my: 3 }} />

                <Typography component="h2" variant="h5" gutterBottom>
                    Invite New User
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Invite a new Administrator, Physician (QHP), or Clinical Staff member to join {currentTenantName}. They will receive an email with a link to complete their registration.
                </Typography>

                {inviteUserMutation.isError && fieldErrors.form && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {fieldErrors.form}
                    </Alert>
                )}
                {inviteSuccessMessage && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        {inviteSuccessMessage}
                    </Alert>
                )}

                <Box component="form" onSubmit={handleInviteUser} noValidate>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Invitee Email"
                                variant="outlined"
                                value={inviteEmail}
                                onChange={(e) => setInviteEmail(e.target.value)}
                                error={!!fieldErrors.email}
                                helperText={fieldErrors.email}
                                required
                                disabled={inviteUserMutation.isPending}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                select
                                fullWidth
                                label="Assign Role"
                                variant="outlined"
                                value={inviteRole}
                                onChange={(e) => setInviteRole(e.target.value)}
                                error={!!fieldErrors.role}
                                helperText={fieldErrors.role}
                                required
                                disabled={inviteUserMutation.isPending}
                            >
                                <MenuItem value="" disabled><em>Select a role</em></MenuItem>
                                {staffRoles.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Invitee First Name (Optional)"
                                variant="outlined"
                                value={inviteFirstName}
                                onChange={(e) => setInviteFirstName(e.target.value)}
                                disabled={inviteUserMutation.isPending}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Invitee Last Name (Optional)"
                                variant="outlined"
                                value={inviteLastName}
                                onChange={(e) => setInviteLastName(e.target.value)}
                                disabled={inviteUserMutation.isPending}
                            />
                        </Grid>
                    </Grid>
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        sx={{ mt: 3, position: 'relative' }}
                        disabled={inviteUserMutation.isPending}
                    >
                        Send Invitation
                        {inviteUserMutation.isPending && (
                            <CircularProgress
                                size={24}
                                sx={{ position: 'absolute', top: '50%', left: '50%', marginTop: '-12px', marginLeft: '-12px' }}
                            />
                        )}
                    </Button>
                </Box>
            </Paper>
            {selectedTenant?.tenant_id ? (
                <>
                    <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 }, mt: 4 }}>
                        {/* CORRECTED: This prop now uses the correct tenant ID source */}
                        <BrandingManager tenantId={selectedTenant.tenant_id} />
                    </Paper>
                </>
            ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <CircularProgress />
                </Box>
            )}
        </Container>
    );
}

export default TenantSettings;